'use client'

import { useSetAtom } from 'jotai'

import { expirationTsMsToExpiry } from '@/features/chart/lib/utils/option-util'
import { WatchlistWizard } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard'
import {
  type OpenInstrumentTabParams,
  useWatchlistTab,
} from '@/features/dashboard/hook/use-watchlist-tab'
import { instrumentTabType } from '@/features/dashboard/lib/instrument-tab-type'
import { toExchangeString } from '@/lib/proto/util'
import type { ChartCompareMode } from '@/state/chart-compare'
import { glowTriggersAtom, triggerCardGlow } from '@/state/linking'
import { Instrument } from '@/types/generated/common/types'

interface WatchListPickerProps {
  cardId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** If provided, replaces this watchList index instead of appending */
  replaceIndex?: number
  /** Pre-fills the search box when the picker opens (type-to-search on the chart). */
  initialQuery?: string
  /** Opened from a chart: lets a pick land as a compare instead of a new tab. */
  chartCompare?: {
    onCompare: (instrument: Instrument, mode: ChartCompareMode) => void
    modes?: ChartCompareMode[]
  }
}

/** The tab an instrument picked in the wizard opens as. */
export function instrumentTabParams(instrument: Instrument): OpenInstrumentTabParams {
  const type = instrumentTabType(instrument)
  return {
    symbol: instrument.symbol,
    type,
    exchange: toExchangeString(instrument.exchange),
    pricePrecision: instrument.pricePrecision || undefined,
    tickSize: instrument.tickSize || undefined,
    ...(type === 'options'
      ? {
          expiry: expirationTsMsToExpiry(instrument.expirationTsMs ?? '0'),
          expirationTsMs: instrument.expirationTsMs,
        }
      : {}),
  }
}

export function WatchListPicker({
  cardId,
  open,
  onOpenChange,
  replaceIndex,
  initialQuery,
  chartCompare,
}: WatchListPickerProps) {
  const { openInstrumentTab } = useWatchlistTab(cardId)
  const setGlowTriggers = useSetAtom(glowTriggersAtom)

  const handleSelect = (instrument: Instrument) => {
    openInstrumentTab({
      ...instrumentTabParams(instrument),
      ...(replaceIndex !== undefined ? { replaceAtIndex: replaceIndex } : {}),
    })
    triggerCardGlow(setGlowTriggers, cardId, 'ticker-change')
  }

  return (
    <WatchlistWizard
      open={open}
      onOpenChange={onOpenChange}
      mode="single"
      onSelect={handleSelect}
      initialQuery={initialQuery}
      chartCompare={chartCompare}
    />
  )
}
